// ============================================================
// TMR Portal Admin — approving an access request by granting roles (10 B7d)
// ============================================================
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { Btn } from '../../components/primitives';
import { Icons } from '../../lib/icons';
import { useApproveAccessRequest } from '../../data/authorization/queries';
import type { AccessRequestListItem, AuthorizationRole } from '../../data/authorization/contract';
import { GrantsEditor, grantDraftError, toGrant } from './GrantsEditor';
import type { GrantDraft } from './GrantsEditor';
import { ErrorBanner } from './ErrorBanner';
import { OverlayPortal } from '../../components/OverlayPortal';

/** Props for {@link ApproveAccessModal}. */
interface ApproveAccessModalProps {
  request: AccessRequestListItem;
  /** Roles available to grant — the same catalogue the Papéis screen reads. */
  roles: AuthorizationRole[];
  onClose: () => void;
}

/**
 * Approves one access request: creates the person's account, grants the roles chosen here, and invites
 * them into Clerk (06 §6.1). Approving with no roles at all is allowed — they are invited and can sign in,
 * and the hint says plainly that they will not be able to do anything yet.
 *
 * Composing the grants — scope, expiry, and which scopes an operator may even offer — belongs to
 * {@link GrantsEditor}, shared with the Users screen.
 */
export function ApproveAccessModal({ request, roles, onClose }: ApproveAccessModalProps) {
  const approve = useApproveAccessRequest(request.id);
  const [grants, setGrants] = useState<GrantDraft[]>([]);
  const [touched, setTouched] = useState(false);

  const submit = () => {
    if (grants.some(grantDraftError)) {
      setTouched(true);
      return;
    }
    approve.mutate({ grants: grants.map(toGrant) }, { onSuccess: onClose });
  };

  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <OverlayPortal>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={stop} style={{ width: 640, maxWidth: '100%', maxHeight: '90vh', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-default)' }}>
            <div className="pa-eyebrow" style={{ marginBottom: 4 }}>Aprovar acesso</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, letterSpacing: '-0.01em' }}>{request.name}</h3>
            <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--fg-muted)' }}>{request.email}</p>
          </div>

          <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
            <ErrorBanner error={approve.error} />

            <GrantsEditor
              grants={grants}
              onChange={setGrants}
              roles={roles}
              showErrors={touched}
              emptyHint="Sem papel, a pessoa entra e não vê nada. Pode ser concedido depois."
            />
          </div>

          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border-default)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn variant="secondary" onClick={onClose}>Cancelar</Btn>
            <Btn variant="primary" icon={Icons.check} onClick={submit} disabled={approve.isPending}>
              {approve.isPending ? 'Aprovando…' : 'Aprovar acesso'}
            </Btn>
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
